const PRIMARY = 'var(--app-primary)'
const SECONDARY = 'var(--app-secondary)'
const HIGHLIGHT = 'var(--app-highlight)'
const PRIMARY_HOVER = 'var(--app-primary-hover)'
const PRIMARY_ACTIVE = 'var(--app-primary-active)'
const ACTIVE_BG = 'var(--app-active-bg)'
const HOVER_BG = 'var(--app-hover-bg)'

const ratingColors = {
  unknown: {
    primary: '#D7D7DC',
    text: '#403F40',
  },
  low: {
    primary: '#FFC9D2',
    text: '#80000F',
  },
  medium: {
    primary: '#FFD274',
    text: '#792102',
  },
  high: {
    primary: '#C4E0B1',
    text: '#0D4A15',
  },
  elite: {
    primary: '#75BD74',
    text: '#07220C',
  },
}

export const productColors = {
  primary: PRIMARY,
  primary100: '#B8D7E6',
  secondary: SECONDARY,
  highlight: HIGHLIGHT,
  primaryHover: PRIMARY_HOVER,
  primaryDisabled: PRIMARY,
  primaryActive: PRIMARY_ACTIVE,
  hoverBg: HOVER_BG,
  activeBg: ACTIVE_BG,
  cgBlue: '#03749C',
  skyBlue: '#5CD4EF',
  brownCherry: '#A82F02',
  pumpkin: '#F88011',
  brightYellow: '#FFAB2E',
  vividGamboge: '#FF9900',
  icon: '#9BA9B0',
  iconBadge: {
    color: '#023D67',
    background: '#E0E8FF',
    border: '#CAD7F0',
  },
  gray: {
    50: '#F2F3F7',
    100: '#E5E5E9',
    200: '#D7D7DC',
    300: '#CACACE',
    400: '#BDBCC0',
    500: '#B0AFB3',
    600: '#A3A1A5',
    700: '#959497',
    800: '#888689',
    900: '#7B797C',
  },
  blue: {
    50: '#E2EFF4',
    100: '#B8D7E6',
    200: '#8EBFD6',
    300: '#68A6C5',
    400: '#4D95BC',
    500: '#3385B3',
    600: '#2878A7',
    700: '#1B6897',
    800: '#115886',
    900: '#023D67',
  },
  cyan: {
    50: '#E2F7FF',
    100: '#B5ECFD',
    200: '#85E0FB',
    300: '#53D3F6',
    400: '#2FCAF1',
    500: '#1DC0EB',
    600: '#15B1D7',
    700: '#039BBC',
    800: '#0088A2',
    900: '#006575',
  },
  border: {
    solid: '#DADFF1',
    subtle: '#E2E8F0',
    dashed: '#9BA9B0',
  },
  text: {
    // Note: Starting to remove "dark" and "light" groupings as they are not being used consistently.
    placeholder: '#959DBB',
    bodySubtle: '#516B7B',
    linkInBody: '#03749C',
    header: '#072E45',
    buttonLink: PRIMARY,
    light: {
      header: PRIMARY,
      bodySolid: '#E2EFF4',
      bodySubtle: '#8EBFD6',
      link: PRIMARY_ACTIVE,
      linkInBody: '#03749C',
      buttonLink: PRIMARY,
    },
    dark: {
      black: '#001C2D',
      ink: '#0A051D',
      error: '#85000F',
      header: PRIMARY,
      bodySolid: '#3E4661',
      bodySubtle: '#516B7B',
      link: PRIMARY_ACTIVE,
      buttonLink: PRIMARY,
      title: '#173A4F',
      titleSubtle: '#173A4FB2',
      placeholder: '#6a7499',
      ranking: '#403F40',
    },
    state: {
      info: '#072E45',
      success: '#105E1D',
      warning: '#A22C02',
      error: '#BC0017',
    },
  },
  // state and tags have same colors
  state: {
    subtle: {
      info: '#E2EFF4',
      success: '#C4E0B1',
      warning: '#FFD274',
      error: '#FFC9D2',
    },
    solid: {
      info: '#023D67',
      success: '#105E1D',
      warning: '#E10014',
      error: '#CA0024',
      errorHover: '#A50020',
      errorActive: '#85000F',
    },
  },
  button: {
    text: PRIMARY,
    primary: {
      500: PRIMARY,
      600: PRIMARY_HOVER,
    },
  },
  table: {
    hover: '#F1FBFF',
  },
  rating: ratingColors,
  progress: {
    500: SECONDARY,
    200: '#F2F3F7',
  },
  experiments: {
    tone: {
      neutral: {
        bg: '#E4E3E7',
        color: '#09051B',
        mutedColor: '#4A4458',
      },
      info: {
        bg: '#DDE4FB',
        color: '#4A16E9',
      },
      success: {
        bg: '#E5FAEA',
        color: '#36633B',
      },
      warning: {
        bg: '#FFF4D6',
        color: '#7A5300',
      },
      error: {
        bg: '#FAEBD8',
        color: '#8B321E',
      },
    },
  },
  gradient: `linear-gradient(180deg, color-mix(in srgb, var(--app-secondary) 33%, transparent), color-mix(in srgb, var(--app-secondary) 80%, transparent) 100%)`,
}

export const baseColors = {
  orange: {
    50: '#FDF3E7',
    100: '#EDECE0',
    400: '#FB8C00',
    500: '#E10014',
    600: '#FB8C00',
    700: '#EF860A',
  },
  red: {
    100: '#F3DADA',
    450: '#E53E3E',
    500: '#F41A28',
  },
  blue: {
    50: '#EFF6FF',
    100: '#DBEAFE',
    400: '#60A5FA',
    500: '#114F73',
    600: '#0E405D',
    700: '#0A3047',
    900: '#011724',
  },
  royalBlue: {
    50: '#F6F9FE',
    100: '#EEF3FD', // secondary
    200: '#D3E0FB',
    300: '#B3C8F8',
    400: '#719AF2',
    500: '#2563EB', // primary
    501: 'rgba(37, 99, 235, 0.1)',
    502: 'rgba(37, 99, 235, 0.2)',
    506: 'rgba(37, 99, 235, 0.6)',
    507: 'rgba(37, 99, 235, 0.7)',
    600: '#1248C0', // hover
  },
  purple: {
    500: '#6A2098',
  },
  gray: {
    50: '#F2F3F7',
    100: '#FAFAFA',
    125: '#F5F6F6',
    150: '#F4F4F4',
    200: '#F9F9FA',
    300: '#E9EBED',
    400: '#BDBDBD',
    425: '#B8B8B8',
    450: '#ABAEB4',
    500: '#E0E0E0',
    525: '#94A3B8',
    550: '#9E9E9E',
    600: '#546E7A',
    700: '#455A64',
    750: '#C9D1DB',
    900: '#212121',
  },
  blueGray: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    250: '#D4DAE3',
    300: '#CBD5E1',
    400: '#94A3B8',
    401: '#F2F5F7',
    403: 'rgba(148, 163, 184, 0.3)',
    404: 'rgba(148, 163, 184, 0.4)',
    405: 'rgba(148, 163, 184, 0.5);',
    406: 'rgba(148, 163, 184, 0.6);',
    450: '#95A3B8',
    500: '#64748B',
    600: '#475569',
    650: '#344156',
    700: '#072E45',
    701: 'rgba(7, 46, 69, 0.1)',
    702: 'rgba(7, 46, 69, 0.2)',
    705: 'rgba(7, 46, 69, 0.5)',
    706: 'rgba(7, 46, 69, 0.6)',
    707: 'rgba(7, 46, 69, 0.7)',
    800: '#1E293B',
    900: '#0F172A',
    907: 'rgba(15, 23, 42, 0.7)',
  },
  graphiqlGray: {
    100: '#F6F7F8',
    200: '#EEEEEE',
    300: '#E0E0E0',
  },
  green: {
    50: '#ECFDF5',
    500: '#10B981',
    550: '#16A34A',
    600: '#059669',
  },
  orangeAlpha: {
    400: 'rgba(251, 140, 0, 0.1)',
    800: '#37474F',
    900: '#263238',
    950: '#212121',
  },
  teal: {
    50: '#E0F2F1',
    600: '#00897B',
  },
  progressBlue: {
    500: '#2563EB',
  },
  progressGreen: {
    500: '#059669',
  },
  progressOrange: {
    500: '#EA580C',
  },
  tabBlueGray: {
    100: '#E2E8F0',
    700: '#072E45',
  },
  metabaseGreen: {
    500: '#84BB4C',
  },
  metabaseRed: {
    500: '#ED6E6E',
  },
  metabaseBlue: {
    500: '#519EE2',
  },
  metabaseTeal: {
    500: '#114F73',
  },
  state: {
    subtle: {
      info: '#E2F7FF',
      warning: '#FFD597',
    },
    solid: {
      info: '#023D67',
      warning: '#E10014',
    },
  },
  text: {
    state: {
      info: '#072E45',
      warning: '#A22C02',
    },
  },
}

// Rich text editor color palette
export const editorColors = {
  // Standard palette colors
  palette: {
    black: '#000000',
    white: '#ffffff',
    red: {
      solid: '#e60000',
      light: '#facccc',
      medium: '#f06666',
      dark: '#a10000',
      darker: '#5c0000',
    },
    orange: {
      solid: '#ff9900',
      light: '#ffebcc',
      medium: '#ffc266',
      dark: '#b26b00',
      darker: '#663d00',
    },
    yellow: {
      solid: '#ffff00',
      light: '#ffffcc',
      medium: '#ffff66',
      dark: '#b2b200',
      darker: '#666600',
    },
    green: {
      solid: '#008a00',
      light: '#cce8cc',
      medium: '#66b966',
      dark: '#006100',
      darker: '#003700',
    },
    blue: {
      solid: '#0066cc',
      light: '#cce0f5',
      medium: '#66a3e0',
      dark: '#0047b2',
      darker: '#002966',
    },
    purple: {
      solid: '#9933ff',
      light: '#ebd6ff',
      medium: '#c285ff',
      dark: '#6b24b2',
      darker: '#3d1466',
    },
    gray: {
      light: '#bbbbbb',
      medium: '#888888',
      dark: '#444444',
    },
  },
}

// Flat array of editor colors for toolbar color picker
export const editorColorPalette = [
  // Faros brand colors first (using existing theme definitions)
  baseColors.blueGray[700], // Header
  productColors.text.dark.bodySolid, // Body Solid
  productColors.text.dark.bodySubtle, // Body Subtle
  productColors.cgBlue, // Link
  // Standard colors
  editorColors.palette.black,
  editorColors.palette.red.solid,
  editorColors.palette.orange.solid,
  editorColors.palette.yellow.solid,
  editorColors.palette.green.solid,
  editorColors.palette.blue.solid,
  editorColors.palette.purple.solid,
  editorColors.palette.white,
  editorColors.palette.red.light,
  editorColors.palette.orange.light,
  editorColors.palette.yellow.light,
  editorColors.palette.green.light,
  editorColors.palette.blue.light,
  editorColors.palette.purple.light,
  editorColors.palette.gray.light,
  editorColors.palette.red.medium,
  editorColors.palette.orange.medium,
  editorColors.palette.yellow.medium,
  editorColors.palette.green.medium,
  editorColors.palette.blue.medium,
  editorColors.palette.purple.medium,
  editorColors.palette.gray.medium,
  editorColors.palette.red.dark,
  editorColors.palette.orange.dark,
  editorColors.palette.yellow.dark,
  editorColors.palette.green.dark,
  editorColors.palette.blue.dark,
  editorColors.palette.purple.dark,
  editorColors.palette.gray.dark,
  editorColors.palette.red.darker,
  editorColors.palette.orange.darker,
  editorColors.palette.yellow.darker,
  editorColors.palette.green.darker,
  editorColors.palette.blue.darker,
  editorColors.palette.purple.darker,
]

export const colors = {
  product: productColors,
  editor: editorColors,
  ...baseColors,
}
